import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, FormControl, Validators, AbstractControl } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, of, switchMap } from 'rxjs';
import { NotificationService } from '../notification.service';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

export interface NotificationData {
  name: string;
  notificationType: string;
  disabled: string;
  rules: any[];
  subscribers: any[];
  notifCurrentId?: any;
  createdBy?: string,
  updatedBy?: string,
}

@Component({
    selector: 'app-notification-dialog',
    templateUrl: './notification-dialog.component.html',
    styleUrls: ['./notification-dialog.component.scss'],
    standalone: false // NOSONAR
})
export class NotificationDialogComponent implements OnInit{
  notificationForm: any;
  notificationTypes = ['AET', 'BRT', 'CXT']; // Example types
  availableFields = ['Event Type', 'Event Plant']; // Example fields
  newUserEmail = ''; // Store email input value
  userSearchControl = new FormControl('');
  filteredUsers: any = [];
  ruleOptions: any = [];

  constructor(
    public dialogRef: MatDialogRef<NotificationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: NotificationData,
    private readonly notificationService: NotificationService,
    private readonly fb: FormBuilder
  ) {

    this.notificationForm = this.fb.group({
      name: [data?.name || '', [Validators.required]], 
      notificationType: [{ value: data?.notificationType || '', disabled: true }],
      disabled: [data?.disabled || '',[Validators.required]],
      rules: this.fb.array([],this.minOneItemRequired("rules")),
      subscribers: this.fb.array([],this.minOneItemRequired("subscribers")),
      notifCurrentId: [data?.notifCurrentId || ''],
      createdBy: [data?.createdBy || ''],
      updatedBy: [data?.updatedBy || ''],
    });

    if (data?.subscribers) {
      this.getUsersByIds(data?.subscribers);
    }

    if (data?.rules?.length) {
      this.initializeForm(data?.rules);
    } else {
      this.initializeForm([
        {
          condition: 'AND',
          fieldId: '',
          fieldOper: '',
          fieldValue: '',
          fieldType: '',
          filterLevel: 0,
          filterSeq: 0,
        },
      ]);
    }

    // Listen for input changes in the search field
    this.userSearchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value =>
        value.length > 1
          ? this.fetchUsers(value).pipe(catchError(() => of([])))
          : of([])
      )
    ).subscribe(users => {
        this.filteredUsers = users;
    });
  }

  minOneItemRequired(arrayName: string){
    return(formArray:AbstractControl)=>{
      const arr = formArray as FormArray;
      return arr.length>0 ? null: {[`${arrayName}Required`]:true};
    }
  }
  ngOnInit() {
    this.getCriteriaFilters();
  }

  // Convert flat data into nested structure
  initializeForm(data: any) {
    const structuredData = this.buildNestedStructure(data);
    structuredData.group.forEach(group => {
      this.rules.push(this.createGroupFormGroup(group));
    });
  }

  // Convert flat array into nested object structure
  buildNestedStructure(data: any) {
    const groupedData: any = { group: [] };
    const levelMap: any = {};

    data.forEach(item => {
      if (!levelMap[item.filterLevel]) {
        levelMap[item.filterLevel] = { rules: [], group: [] };
        if (item.filterLevel == 0) { // NOSONAR
          groupedData.group.push(levelMap[item.filterLevel]);
        } else {
          const parentLevel = levelMap[item.filterLevel - 1];
          if (parentLevel) {
            parentLevel.group.push(levelMap[item.filterLevel]);
          }
        }
      }
      levelMap[item.filterLevel].rules.push(item);
    });

    return groupedData;
  }

  // Create a FormGroup for each group level
  createGroupFormGroup(groupData: any): FormGroup {
    return this.fb.group({
      condition: groupData.rules.length ? groupData.rules[0].condition : 'AND',
      rules: this.fb.array(groupData.rules.map(rule => this.createRuleFormGroup(rule)),this.minOneItemRequired("rules")),
      group: this.fb.array(groupData.group.map(subGroup => this.createGroupFormGroup(subGroup)))
    });
  }

  // Create a FormGroup for each rule
  createRuleFormGroup(ruleData: any): FormGroup {
    return this.fb.group({
      fieldId: [ruleData.fieldId,[Validators.required]],
      fieldOper: [ruleData.fieldOper,[Validators.required]],
      fieldValue: [ruleData.fieldValue,[Validators.required]],
      fieldType: [ruleData.fieldType],
      filterSeq: [ruleData.filterSeq],
      filterLevel: [ruleData.filterLevel]
    });
  }

  fetchUsers(query: string) {
    return this.notificationService.searchUsers(query); // Backend API call
  }

  private hasSubscriberByEmail(email: string): boolean {
    const normalized = (email || '').trim().toLowerCase();
    if (!normalized) {
      return false;
    }
    return this.subscriberList.controls.some((ctrl: AbstractControl) => {
      const value = ctrl?.value || {};
      const candidate = (value.userEmail || value.email || '').trim().toLowerCase();
      return candidate === normalized;
    });
  }

  selectUser(user: any) {
    const actualEmail = user.email;                          // preserve APM email
    if (this.hasSubscriberByEmail(actualEmail)) {
      this.filteredUsers = [];
      this.userSearchControl.setValue('');
      return;
    }
    user.email = `${user.firstName} ${user.lastName}`;      // display name for chip
    user.userEmail = actualEmail;                           // actual email for backend
    this.subscriberList.push(this.createSubscriber(user, 'USER'));
    this.filteredUsers=[];
    this.userSearchControl.setValue(''); // Clear input field after selection
  }

  transformValues(data: any) {
    return data.map(item => ({
      ...item,
      values: Object.entries(item.values).map(([key, value]) => ({ key, value }))
    }));
  }

  getCriteriaFilters() {
    this.notificationService.getCriteriaFilters().subscribe({
      next: (data: any) => {
        if (data?.length) {
          this.ruleOptions = this.transformValues(data);
        } else {
          this.ruleOptions = [];
        }
      },
      error: (_err) => {
        // do nothing 
      }
    });
  }

  // Getters
  get rules(): FormArray {
    return this.notificationForm.get('rules') as FormArray;
  }

  get subscriberList(): FormArray {
    return this.notificationForm.get('subscribers') as FormArray;
  }

  // Create Subscriber
  createSubscriber(data: any, type?: any) {
      const resolvedType = data?.subType ? data?.subType : type;
      return this.fb.group({
        userId: [data?.userId],
        subType: [resolvedType],
        email: [data?.email],
        userEmail: [resolvedType == 'USER' ? (data?.userEmail || data?.email || null) : (data?.userEmail || null)],
      });
  }

  // Add Subscriber
  addNewUser() {
    const enteredEmail = this.newUserEmail.trim();
    if (enteredEmail) {
      if (this.hasSubscriberByEmail(enteredEmail)) {
        this.newUserEmail = '';
        return;
      }
      this.subscriberList.push(this.createSubscriber({ email: enteredEmail }, 'EMAIL'));
      this.newUserEmail = ''; // Clear input
    }
  }

  // Remove Subscriber
  removeSubscriber(index: number) {
    this.subscriberList.removeAt(index);
  }

  flattenFilters(
    filters: any,
    filterLevel = 0,
    seqTracker: { [key: number]: number } = {},
    result: any[] = [],
    parentCondition: string = "" // Pass parent condition
  ) {
    if (!filters) {return result;} // Ensure filters exist

    if (!(filterLevel in seqTracker)) {
      seqTracker[filterLevel] = 0; // Initialize sequence for this level
    }

    // Use the current filter's condition (for root level too)
    const currentCondition = filters.condition || parentCondition;

    if (Array.isArray(filters.rules)) {
      filters.rules.forEach((rule: any) => {
        result.push({
          ...rule,
          filterLevel,
          filterSeq: seqTracker[filterLevel]++, // Assign seq and increment
          condition: currentCondition // Assign condition for all levels
        });
      });
    }

    if (Array.isArray(filters.group)) {
      filters.group.forEach((group: any) => {
        this.flattenFilters(group, filterLevel + 1, seqTracker, result, group.condition || currentCondition);
      });
    }

    return result;
  }

  transformSubData(data: any[]) {
    return data.map(({ subType, ...rest }) => {
      if (subType == 'USER') { // NOSONAR
        const { email, userEmail, ...filtered } = rest;
        return { subType, email: userEmail || email, ...filtered }; // send actual APM email to backend
      } else if (subType == 'EMAIL') { // NOSONAR
        const { userId, userEmail, ...filtered } = rest; // Remove userId
        return { subType, ...filtered };
      }
      return { subType, ...rest }; // Default case (if subType is something else)
    });
  }

  // Save and Close Dialog
  onSave() {
    if (this.notificationForm.valid) {
      if(this.notificationForm?.value?.rules?.length && !this.notificationForm?.value?.rules[0].rules?.length){
        this.notificationForm.markAllAsTouched();
        return
      }
      const data: any = this.notificationForm.getRawValue();
    let flattenedRules: any = [];

    data?.rules.forEach(filter => {
      flattenedRules = [...flattenedRules, ...this.flattenFilters(filter)];
    })

    const modifiedPayload: any = {
      notification: {
        name: data?.name,
        notificationType: data?.notificationType,
        disabled: data?.disabled ,
      },
      rules: flattenedRules,
      subscribers: this.transformSubData(data?.subscribers),
    }

    if (data?.notifCurrentId) {
      modifiedPayload.notification.notifCurrentId = data?.notifCurrentId;
      modifiedPayload.notification.createdBy = data?.createdBy;
      modifiedPayload.notification.updatedBy = data?.updatedBy;
    }

    console.info(modifiedPayload, 'modifiedPayload')
    this.dialogRef.close(modifiedPayload);
    } else {
      this.notificationForm.markAllAsTouched();
      console.info('Form is invalid!');
    }
    
  }

  // Cancel
  onCancel() {
    this.dialogRef.close();
  }
  getUsersByIds(users: any) {
    const upns = users
    .filter(user => user.subType == 'USER') // NOSONAR
    .map(user => user.email);
    const usersGroup = {
      USER: users.filter(user => user.subType == 'USER'), // NOSONAR
      EMAIL: users.filter(user => user.subType == 'EMAIL') // NOSONAR
    };
    if (upns?.length) {
      this.notificationService.getUsersByIds(upns).subscribe({
        next: (data: any) => {
          const responseList = Array.isArray(data) ? data : [];
          const foundUpnSet = new Set<string>();
          if (responseList.length) {
            for (let i = 0; i < responseList.length; i++) {
              const upn = responseList[i]?.userPrincipalName || responseList[i]?.email;
              const original = usersGroup.USER.find((user) => user?.email == upn);
              foundUpnSet.add((upn || '').toLowerCase());
              responseList[i].email = `${responseList[i].firstName} ${responseList[i].lastName}`;
              responseList[i].userEmail = original?.email || upn;
              if (!this.hasSubscriberByEmail(responseList[i].userEmail)) {
                this.subscriberList.push(this.createSubscriber(responseList[i], 'USER'));
              }
            }
          }

          usersGroup.USER.forEach((existingUser) => {
            const existingUpn = (existingUser?.email || '').toLowerCase();
            if (!foundUpnSet.has(existingUpn) && !this.hasSubscriberByEmail(existingUser?.email)) {
              this.subscriberList.push(this.createSubscriber({
                ...existingUser,
                userEmail: existingUser?.email,
              }, 'USER'));
            }
          });
        },
        error: (_err) => { // do nothing 
          usersGroup.USER.forEach((existingUser) => {
            if (!this.hasSubscriberByEmail(existingUser?.email)) {
              this.subscriberList.push(this.createSubscriber({
                ...existingUser,
                userEmail: existingUser?.email,
              }, 'USER'));
            }
          });
        }
      });
    }
    if(usersGroup?.EMAIL?.length)
    {
      for (let i = 0; i < usersGroup.EMAIL.length; i++) {
        if (!this.hasSubscriberByEmail(usersGroup.EMAIL[i]?.email)) {
          this.subscriberList.push(this.createSubscriber(usersGroup.EMAIL[i], 'EMAIL'));
        }
      }
    }

  }
}